import React, { useState, useRef } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';

import { stocktakingApi } from 'api';

const MAX_POLL_ATTEMPTS = 60;
const POLL_INTERVAL_MS = 1000;

const CreateStocktakingModal = ({ show, onHide, onSuccess }) => {
  const [scope, setScope] = useState('full');
  const [skuInput, setSkuInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState(null); // null | 'creating' | 'initializing'
  const [errors, setErrors] = useState([]);
  const abortRef = useRef(false);

  const handleClose = () => {
    if (loading) return;
    setScope('full');
    setSkuInput('');
    setErrors([]);
    setPhase(null);
    onHide();
  };

  const pollInitialize = async stocktakingId => {
    for (let i = 0; i < MAX_POLL_ATTEMPTS; i++) {
      if (abortRef.current) return null;
      try {
        const result = await stocktakingApi.initialize(stocktakingId);
        return result;
      } catch (error) {
        const status = error?.response?.status;
        const data = error?.response?.data;
        const text =
          typeof data === 'string' ? data : JSON.stringify(data || '');
        const lower = text.toLowerCase();
        const isTaskRunning = lower.includes('task is running');
        const isAlreadyInitialized = lower.includes('lines already exist');

        if (isAlreadyInitialized) {
          return await stocktakingApi.getById(stocktakingId);
        }

        if (status === 400 && isTaskRunning) {
          await new Promise(r => setTimeout(r, POLL_INTERVAL_MS));
          continue;
        }

        throw error;
      }
    }
    throw new Error('POLL_TIMEOUT');
  };

  const handleCreate = async () => {
    setErrors([]);
    setLoading(true);
    abortRef.current = false;

    try {
      // Step 1: Create stocktaking (Lock activated, no lines yet)
      const body = {};
      if (scope === 'partial') {
        const skuList = skuInput
          .split(/[,\n]+/)
          .map(s => s.trim())
          .filter(Boolean);
        if (skuList.length === 0) {
          setErrors(['Please enter at least one SKU.']);
          setLoading(false);
          return;
        }
        body.sku_list = skuList;
      }

      setPhase('creating');
      const created = await stocktakingApi.create(body);
      const stocktakingId = created.id;

      // Step 2: Poll initialize (wait for background tasks, then create lines)
      setPhase('initializing');
      const result = await pollInitialize(stocktakingId);
      if (!result) {
        setLoading(false);
        setPhase(null);
        return;
      }

      toast.success('Stocktaking created successfully.');
      setLoading(false);
      setPhase(null);
      setScope('full');
      setSkuInput('');
      setErrors([]);
      onHide();
      onSuccess(result);
    } catch (error) {
      if (error.message === 'POLL_TIMEOUT') {
        setErrors([
          'Background tasks are taking too long. Please try again later.',
        ]);
        setLoading(false);
        setPhase(null);
        return;
      }
      const data = error?.response?.data;
      if (Array.isArray(data)) {
        setErrors(data);
      } else if (data && typeof data === 'object') {
        const msgs = Object.values(data).flat();
        setErrors(msgs);
      } else {
        setErrors(['Failed to create stocktaking.']);
      }
      setLoading(false);
      setPhase(null);
    }
  };

  const loadingText =
    phase === 'creating'
      ? 'Creating...'
      : phase === 'initializing'
      ? 'Waiting for background tasks...'
      : 'Processing...';

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop={loading ? 'static' : true}
      keyboard={!loading}
    >
      <Modal.Header closeButton={!loading}>
        <Modal.Title>Start Stocktaking</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant="warning" className="mb-3">
          Inventory-related operations will be paused during stocktaking.
        </Alert>

        {errors.length > 0 && (
          <Alert variant="danger">
            {errors.map((err, i) => (
              <div key={i}>{err}</div>
            ))}
          </Alert>
        )}

        {phase === 'initializing' && (
          <Alert variant="info" className="d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" />
            Waiting for background tasks to complete...
          </Alert>
        )}

        <Form.Group className="mb-3">
          <Form.Label className="fw-semibold">Scope</Form.Label>
          <div>
            <Form.Check
              inline
              type="radio"
              label="Full"
              name="scope"
              checked={scope === 'full'}
              onChange={() => setScope('full')}
              disabled={loading}
            />
            <Form.Check
              inline
              type="radio"
              label="Partial"
              name="scope"
              checked={scope === 'partial'}
              onChange={() => setScope('partial')}
              disabled={loading}
            />
          </div>
        </Form.Group>

        {scope === 'partial' && (
          <Form.Group className="mb-3">
            <Form.Label>
              SKU List <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              as="textarea"
              rows={4}
              placeholder="Enter SKUs separated by commas or new lines"
              value={skuInput}
              onChange={e => setSkuInput(e.target.value)}
              disabled={loading}
            />
            <Form.Text className="text-muted">
              e.g. SKU-001, SKU-002, SKU-003
            </Form.Text>
          </Form.Group>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleCreate} disabled={loading}>
          {loading ? loadingText : 'Start Stocktaking'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CreateStocktakingModal;
